// Information tab: the title and the description — localized, so they are read
// and written in the language picked at the top of the dialog, the description in the
// framework's HTML editor — the content type browser, the catalog's own pieces
// (duration, age rating, presentation order), the product's fields — pages,
// ISBN, cover type, whatever its content type asks for — and the product id
// at the bottom.
import React from "react"
import Button from "aldoo-ra/Button"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { ClipboardIcon, TrashIcon } from "@heroicons/react/24/outline"
import { Field, TextInput, errorMessage } from "../../../AdminShared/ui"
import { LanguageTag } from "aldoo-ra/Admin/Store/Products/EditingLanguage"
import { SoftDeleteProductByPid } from "../../../AdminShared/api"
import LocalizedHtmlField from "../shared/LocalizedHtmlField"
import ProductFieldsEditor from "../shared/ProductFieldsEditor"
import Typography from "aldoo-ra/Typography"
import { HelperText } from "../shared/shared"
import useProductEditorTexts from "aldoo-ra/CMSModels/StoreProduct/useProductEditorTexts"
import { useVendorVisibilityContext } from "../shared/vendor-visibility"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"

// the catalog's own fields sit in the same cell the CMS fields are given, so
// the tab reads as one grid rather than as two layouts side by side. Defined
// here, not in the component: a component built while rendering is a new type
// every render, and React remounts it — which loses the focus of the input
// being typed into.
const Cell = ({ full = false, children }) => (
  // a phone has room for one field a row, and no margin to spare
  <div className={`mb-4 md:pr-4 ${full ? "w-full" : "w-full md:w-1/2"}`}>{children}</div>
)

export default function InformationTab({ categories, addCategory, editor, pid, isAdmin, shouldShowField, contentType, isProductEditor, languages, close, storeValues = {}, syncValues, extendedFields = [] }) {
  const { read, set, language } = editor
  const t = useProductEditorTexts()
  // a merchant has no Products → Restore: a deleted product comes back through Aldoo
  const { vendorEditor } = useVendorVisibilityContext()
  // a label of a field that changes with the dialog's language, with the help center's words on it (helpKey)
  const localized = (label, helpKey = null) => (
    <span className="inline-flex items-center gap-1.5">
      {label}
      <LanguageTag language={language} />
      {helpKey && <HelpHint helpKey={helpKey} />}
    </span>
  )

  // deleting the product is the last thing this tab offers: it is the product
  // itself, not one of its fields. It is a soft delete — the product leaves
  // the store and the listings and waits in Products → Restore; deleting it
  // for good is that dialog's own Delete Forever
  const remove = () =>
    showPromptDialog({
      variant: "admin",
      title: t`Delete product`,
      message: vendorEditor
        ? t("Delete %s? It leaves the store and the listings at once. Aldoo can bring it back within 30 days: write to us.", pid)
        : t("Delete %s? It leaves the store and the listings at once. You can bring it back from Products → Restore, or delete it for good there.", pid),
      confirmText: t`Delete`,
      cancelText: t`Cancel`,
      onConfirm: async () => {
        const res = await SoftDeleteProductByPid(pid)
        const error = errorMessage(res)
        if (error) return showNotification({ message: error, className: "bg-admin_error text-white" })
        showNotification({ message: t("%s deleted", pid), className: "bg-admin_success text-white" })
        close?.()
      },
    })

  return (
    <div className="flex flex-row flex-wrap">
      {/* the title and the description live in the product's localization
          entries, one per language: the CMS field would edit the copy the
          document keeps of the primary language and leave the rest unreachable */}
      {shouldShowField("name") && (
        <Cell>
          <Field label={localized(t`Title`, "vendor.product#title")}>
            <TextInput value={editor.readOwn("name") ?? ""} placeholder={read("name") || ""} onChange={(v) => set("name", v)} disabled={!isProductEditor} data-testid="field-name" />
          </Field>
        </Cell>
      )}
      {shouldShowField("description") && (
        <Cell full>
          <LocalizedHtmlField
            label={t`Description`}
            helpKey="vendor.product#description"
            field="description"
            editor={editor}
            disabled={!isProductEditor}
            title={read("name") || t`Description`}
          />
        </Cell>
      )}

      {/* the duration and the age rating are fields like any other (below): a type that wants them has them
          in its field presets */}
      {/* the product's fields, in the language above: what the content type
          asks for and what is added here; the store page lists them */}
      <Cell full>
        <Field label={localized(t`Fields`, "vendor.product#fields")}>
          <ProductFieldsEditor
            editor={editor}
            contentType={contentType}
            categories={categories}
            onCategoryAdded={addCategory}
            disabled={!isProductEditor}
            // the SKU, the EAN (other extensions' fields of the store product): listed here, not twice
            bound={Object.fromEntries(extendedFields.map((field) => [field.id, { label: field.label || field.id, value: storeValues?.[field.id] ?? "", onChange: (v) => syncValues?.({ [field.id]: v }) }]))}
          />
        </Field>
      </Cell>
      {/* the order on the author pages: a setting, after what the product is */}
      {shouldShowField("presentationOrder") && (
        <Cell>
          <Field label={<span className="inline-flex items-center gap-1.5">{t`Presentation Order`}<HelpHint helpKey="vendor.product#presentation-order" /></span>}>
            <TextInput type="number" value={read("presentationOrder") ?? 0} onChange={(v) => set("presentationOrder", Number(v) || 0)} disabled={!isProductEditor} />
            <HelperText>{t`Higher numbers appear first on author pages. Use to prioritize featured books (default: 0)`}</HelperText>
          </Field>
        </Cell>
      )}

      {/* the id the store and every old client address the product by: it is
          read-only and never edited here, so it sits last. It is copied often
          enough (a support ticket, a link, a query) to keep the one-click copy
          the dialog's header used to offer */}
      <Cell>
        <Field label={<span className="inline-flex items-center gap-1.5">{t`Product ID`}<HelpHint helpKey="vendor.product#product-id" /></span>}>
          <div className="flex items-center gap-2">
            <TextInput value={read("_id") ?? ""} readOnly disabled data-testid="field-pid" className="flex-1" />
            <Button
              variant="admin_secondary"
              className="h-[38px] px-3"
              title={t`Copy the product id`}
              onClick={() => {
                navigator.clipboard?.writeText(read("_id") ?? "")
                showNotification({ message: t`Product ID copied to clipboard`, className: "bg-admin_success text-white" })
              }}
              data-testid="copy-pid"
            >
              <ClipboardIcon className="w-4 h-4" />
            </Button>
          </div>
        </Field>
      </Cell>

      {isAdmin && (
        <Cell full>
          <div className="flex items-center justify-between gap-4 p-4 border border-red-300 rounded-lg">
            <div className="flex flex-col">
              <Typography variant="admin_body1" className="font-medium">{t`Delete this product`}</Typography>
              <HelperText>{vendorEditor ? t`It leaves the store at once. Aldoo can bring it back within 30 days: write to us.` : t`It leaves the store at once and waits in Products → Restore, where it can be brought back or deleted for good.`}</HelperText>
            </div>
            <Button variant="admin_primary_red" className="px-3 h-[34px] text-xs" onClick={remove} aria-label="delete-product">
              <TrashIcon className="w-4 h-4" /> {t`Delete`}
            </Button>
          </div>
        </Cell>
      )}
    </div>
  )
}
